import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import Button from '../../../../library/button/button';
import Image from '../../../../library/image/image';
import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import FitMessage from '../../../../components/fit-message/fit-message.one-dt';
import PlpProductBannerSize from '../plp-product-banner-size/plp-product-banner-size';
import ProductListProfileChips from '../product-list-profile-chips/product-list-profile-chips';

import { CMS_BRAND_CONTENT } from '../../graphql/plp-queries';

import {
  useCmsPageContentSlots,
  useFitment,
  useMediaQuery,
  useQuery
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import {
  formatTireSizeParts,
  formatWheelSizeParts
} from '../../../../shared/utils/format/format';
import { getNormalizedProductType } from '../../../../shared/utils/product/product';
import { getResultsText } from '../../utils/results-text-utils';

import { DoesFitEnum } from '../../../../shared/types/graphql-types';
import { FIT } from '../../../../shared/constants/fit';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import './plp-product-banner.scss';

interface TireSizeDetails {
  aspectRatio: string;
  diameter: string;
  width: string;
}

interface TireSize {
  front: TireSizeDetails | null | undefined;
  rear: TireSizeDetails | null | undefined;
}

interface WheelSizeDetails {
  boltPattern: string;
  diameter: string;
  width: string;
}

interface WheelSizes {
  front: WheelSizeDetails | null;
  rear: WheelSizeDetails | null | undefined;
}

interface PlpProductBannerProps {
  brandName?: string;
  brandSeoName?: string;
  doesFitEnum: DoesFitEnum;
  infoMessage?: { id: number };
  isBrandSearch?: boolean;
  isBrandSizeSearch?: boolean;
  isFitmentSearch?: boolean;
  isLoading: boolean;
  isPackageSearch?: boolean;
  isPrebuiltPackageSearch?: boolean;
  isSizeSearch?: boolean;
  isTrailerOrLawnOrAtvOrUtvSearch?: boolean;
  lowestPriceProduct?: { isOnlineOnly: boolean; price: number };
  productType: keyof typeof PRODUCT_TYPES;
  tireSizes?: TireSize | null;
  totalNumberOfResults?: number;
  wheelSizes?: WheelSizes | null;
}

function PlpProductBanner(props: PlpProductBannerProps) {
  const {
    brandName,
    brandSeoName,
    doesFitEnum,
    infoMessage,
    isBrandSearch = false,
    isBrandSizeSearch = false,
    isFitmentSearch = false,
    isLoading = false,
    isPackageSearch = false,
    isPrebuiltPackageSearch = false,
    isSizeSearch = false,
    isTrailerOrLawnOrAtvOrUtvSearch = false,
    lowestPriceProduct,
    productType,
    tireSizes,
    totalNumberOfResults,
    wheelSizes
  } = props;

  const isTires = productType === PRODUCT_TYPES.TIRES;
  const isWheels = productType === PRODUCT_TYPES.WHEELS;
  const isAccessories = productType === PRODUCT_TYPES.ACCESSORIES;

  const { partialFitment, selectedFitment } = useFitment();
  const { isMinLg, isMinSm } = useMediaQuery();
  const { pathname, search } = useLocation();
  const { getContentSlot: getWheelsPlpBannerContentSlot } =
    useCmsPageContentSlots('buy-wheels');

  const brandNameWithoutProdType = brandSeoName?.replace(
    `-${productType?.toLowerCase()}`,
    ''
  );

  const { data: cmsData } = useQuery(CMS_BRAND_CONTENT, {
    skip: !brandNameWithoutProdType && isBrandSearch,
    variables: {
      id: `/buy-tires/${brandNameWithoutProdType?.toLowerCase()}`
    }
  });

  const { contentSlots } = cmsData?.cms?.brandContent || {};

  const getContentSlot = useCallback(
    (name: string = '') =>
      contentSlots?.filter(contentSlot => contentSlot.name === name)?.[0]
        ?.cmsComponents?.[0]?.html,
    [contentSlots]
  );

  const fitment = selectedFitment || partialFitment;
  const {
    frontTireSize: frontSize,
    isStaggered,
    make,
    model,
    rearTireSize: rearSize,
    trim,
    year
  } = fitment || {};

  const isWindShieldWiper =
    pathname?.includes('wipers-catalog') ||
    pathname?.includes('windshield-wipers') ||
    search?.includes('windshieldWiperBladeAccessories');

  const frontTireSize =
    (tireSizes?.front && formatTireSizeParts(tireSizes.front)) || frontSize;
  const rearTireSize =
    (tireSizes?.rear && formatTireSizeParts(tireSizes.rear)) ||
    (isStaggered ? rearSize : '');

  const frontWheelSize =
    wheelSizes?.front && formatWheelSizeParts(wheelSizes.front);
  const rearWheelSize =
    wheelSizes?.rear && formatWheelSizeParts(wheelSizes.rear);

  function handleEnterYourVehicleClick() {
    trackAction('product_fitment_validation_banner');
    window.location.hash = '#/fitment/vehicle/continue-to/close';
  }

  const brandDescription = getContentSlot('description');

  const imageSrcUrl = useMemo(() => {
    if (isPrebuiltPackageSearch) {
      return 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_packages?$PLP-hero$';
    } else if (isTires) {
      return 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_tires?$PLP-hero$';
    } else if (isWheels) {
      return 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_wheels?$PLP-hero$';
    } else if (isAccessories) {
      return isWindShieldWiper
        ? 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_wipers?$PLP-hero$'
        : 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_accessories?$PLP-hero$';
    } else {
      return 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_tires?$PLP-hero$';
    }
  }, [
    isPrebuiltPackageSearch,
    isTires,
    isWheels,
    isAccessories,
    isWindShieldWiper
  ]);

  const getProdType = useMemo(() => {
    if (isPrebuiltPackageSearch) {
      return 'Packages';
    } else if (isWindShieldWiper) {
      return 'Windshield Wipers';
    } else if (
      (isTires || isWheels) &&
      brandName &&
      /tires|wheels/i.test(brandName)
    ) {
      return '';
    } else {
      return productType?.toLowerCase();
    }
  }, [
    isPrebuiltPackageSearch,
    isWindShieldWiper,
    productType,
    isTires,
    isWheels,
    brandName
  ]);

  const isAccessory =
    getNormalizedProductType(productType) === PRODUCT_TYPES.ACCESSORIES;

  const getDisplayText = () => {
    if (!brandName) {
      return getProdType;
    }

    if (isAccessory) {
      return brandName;
    }

    return `${brandName} ${getProdType}`;
  };
  const displayText = getDisplayText();

  function getWheelDisplayText() {
    let text = <>Shopping All Wheels</>;

    if (isBrandSearch) {
      text = (
        <>
          Shopping <strong>{brandName}</strong>
        </>
      );
    } else if (isSizeSearch) {
      text = (
        <>
          Wheel size:{' '}
          <strong>
            {frontWheelSize}
            {rearWheelSize && `/${rearWheelSize}`}
          </strong>
        </>
      );
    } else if (!brandName && fitment) {
      text = (
        <>
          Wheels that fit your{' '}
          <strong>
            {year} {make} {model} {trim}
          </strong>
        </>
      );
    } else if (brandName) {
      text = (
        <>
          Shopping <strong>{displayText}</strong>
        </>
      );
    }

    return text;
  }

  return (
    <>
      {isWheels ? (
        <>
          <div styleName="wheel-container">
            <div
              dangerouslySetInnerHTML={{
                __html: getWheelsPlpBannerContentSlot('wheels-plp-banner')
              }}
            />
            <div styleName="wheel-info">{getWheelDisplayText()}</div>
          </div>
          {!isMinSm && (
            <Typography styleName="mobile-banner" weight="black">
              {totalNumberOfResults}{' '}
              {isBrandSearch &&
                brandName?.replace(
                  `${
                    productType?.charAt(0)?.toUpperCase() +
                    productType?.slice(1)?.toLowerCase()
                  }`,
                  ''
                )}{' '}
              {(() => {
                const parsedQuery = queryString.parse(search);
                const searchText = (parsedQuery.text as string) || '';

                const resultsText = getResultsText(
                  totalNumberOfResults || 0,
                  searchText,
                  isPrebuiltPackageSearch,
                  productType
                );
                return resultsText;
              })()}
              {brandDescription && (
                <Typography styleName="description" tag="div">
                  {brandDescription}
                </Typography>
              )}
              {!isTrailerOrLawnOrAtvOrUtvSearch && (
                <FitMessage
                  data-testid="FitMessage"
                  doesFit={doesFitEnum === FIT}
                  fitment={selectedFitment}
                  infoMessage={infoMessage}
                  isLoading={isLoading}
                  isPackage={isPrebuiltPackageSearch}
                  productType={productType}
                />
              )}
              {!isTrailerOrLawnOrAtvOrUtvSearch && fitment && (
                <ProductListProfileChips showTreadwellProfile={false} />
              )}
            </Typography>
          )}
        </>
      ) : (
        <>
          {isMinSm ? (
            <>
              {isTrailerOrLawnOrAtvOrUtvSearch ? (
                <Typography styleName="category-title" tag="div" variant="H3">
                  Shopping&nbsp;
                  <strong styleName="type">{brandName}</strong>
                </Typography>
              ) : (
                <div
                  styleName={
                    productType !== PRODUCT_TYPES.TIRES
                      ? 'container-bordered'
                      : 'container'
                  }
                >
                  {isMinLg && (
                    <Image
                      alt={productType}
                      height="257"
                      src={imageSrcUrl}
                      styleName="image"
                      width="278"
                    />
                  )}
                  <Typography styleName="content" tag="div">
                    {isBrandSearch || isSizeSearch ? (
                      <Typography styleName="title" tag="div" variant="H3">
                        {isBrandSearch ? (
                          <>
                            Shopping{' '}
                            <strong styleName="type">{brandName}</strong>
                            {lowestPriceProduct?.price && (
                              <span>
                                {', '}
                                starting
                                {lowestPriceProduct?.isOnlineOnly &&
                                  ' online'}{' '}
                                at <strong>${lowestPriceProduct.price}</strong>
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            Shopping{' '}
                            <strong styleName="type">
                              {productType?.toLowerCase()}
                            </strong>{' '}
                            by Size
                            {lowestPriceProduct?.price && (
                              <span>
                                {', '}
                                starting
                                {lowestPriceProduct?.isOnlineOnly &&
                                  ' online'}{' '}
                                at <strong>${lowestPriceProduct.price}</strong>
                              </span>
                            )}
                          </>
                        )}
                      </Typography>
                    ) : (
                      <>
                        <Typography styleName="title" tag="div" variant="H3">
                          Shop All
                          <strong styleName="type">
                            {displayText && ` ${displayText} `}
                          </strong>
                          {fitment && displayText ? 'that fit your' : ''}
                          {lowestPriceProduct?.price && (
                            <span>
                              {fitment && displayText ? ' vehicle,' : ','}{' '}
                              starting
                              {lowestPriceProduct?.isOnlineOnly &&
                                ' online'} at{' '}
                              <strong>${lowestPriceProduct.price}</strong>
                            </span>
                          )}
                        </Typography>
                        {fitment ? (
                          <Typography tag="div" variant="H6">
                            {lowestPriceProduct?.price && 'Tires for your '}
                            <strong>
                              {year} {make} {model} {trim}
                            </strong>{' '}
                            that are available at nearby stores
                          </Typography>
                        ) : (
                          isAccessories && (
                            <Typography tag="div" variant="H6">
                              <Button
                                onClick={handleEnterYourVehicleClick}
                                styleName="enter-vehicle"
                              >
                                enter your vehicle
                              </Button>{' '}
                              to ensure these products fit
                            </Typography>
                          )
                        )}
                      </>
                    )}

                    {brandDescription && (isWheels || isTires) ? (
                      <Typography styleName="description" tag="div">
                        {brandDescription}
                      </Typography>
                    ) : (
                      <>
                        {isWheels && !isPackageSearch && (
                          <Link
                            onClick={() => {
                              trackAction('use_our_wheel_visualizer_click');
                            }}
                            size="small"
                            styleName="wheel-visualizer"
                            to="/wheel-visualizer"
                          >
                            View wheel visualizer
                          </Link>
                        )}
                        {(isTires ||
                          isPrebuiltPackageSearch ||
                          (isWheels && wheelSizes)) &&
                          !isBrandSearch &&
                          (frontTireSize ||
                            rearTireSize ||
                            frontWheelSize ||
                            rearWheelSize) && (
                            <PlpProductBannerSize
                              brandName={isBrandSizeSearch ? brandName : ''}
                              frontSize={
                                isTires || isPrebuiltPackageSearch
                                  ? frontTireSize
                                  : frontWheelSize
                              }
                              isSizeSearch={isSizeSearch}
                              productType={productType}
                              rearSize={
                                isTires || isPrebuiltPackageSearch
                                  ? rearTireSize
                                  : rearWheelSize
                              }
                            />
                          )}
                      </>
                    )}
                  </Typography>
                </div>
              )}
            </>
          ) : (
            <Typography styleName="mobile-banner" weight="black">
              {totalNumberOfResults}{' '}
              {isBrandSearch &&
                brandName?.replace(
                  `${
                    productType?.charAt(0)?.toUpperCase() +
                    productType?.slice(1)?.toLowerCase()
                  }`,
                  ''
                )}{' '}
              {(() => {
                const parsedQuery = queryString.parse(search);
                const searchText = (parsedQuery.text as string) || '';

                const resultsText = getResultsText(
                  totalNumberOfResults || 0,
                  searchText,
                  isPrebuiltPackageSearch,
                  productType
                );
                return resultsText;
              })()}
              {lowestPriceProduct?.price && (
                <span styleName="lowest-price-from">
                  {' '}
                  from <strong>${lowestPriceProduct.price}</strong>
                </span>
              )}
              {brandDescription && (
                <Typography size="small" styleName="description" tag="div">
                  {brandDescription}
                </Typography>
              )}
              {!isBrandSearch &&
                !isFitmentSearch &&
                !isPackageSearch &&
                !isPrebuiltPackageSearch &&
                !isTrailerOrLawnOrAtvOrUtvSearch &&
                (!isSizeSearch && isMinSm ? true : !selectedFitment) &&
                (frontTireSize ||
                  rearTireSize ||
                  frontWheelSize ||
                  rearWheelSize) && (
                  <PlpProductBannerSize
                    brandName={isBrandSizeSearch ? brandName : ''}
                    frontSize={
                      isTires || isPrebuiltPackageSearch
                        ? frontTireSize
                        : frontWheelSize
                    }
                    isSizeSearch={isSizeSearch}
                    productType={productType}
                    rearSize={
                      isTires || isPrebuiltPackageSearch
                        ? rearTireSize
                        : rearWheelSize
                    }
                  />
                )}
              {!isTrailerOrLawnOrAtvOrUtvSearch && (
                <FitMessage
                  data-testid="FitMessage"
                  doesFit={doesFitEnum === FIT}
                  fitment={selectedFitment}
                  infoMessage={infoMessage}
                  isLoading={isLoading}
                  isPackage={isPrebuiltPackageSearch}
                  productType={productType}
                />
              )}
              {!isTrailerOrLawnOrAtvOrUtvSearch && fitment && (
                <ProductListProfileChips showTreadwellProfile={false} />
              )}
            </Typography>
          )}
        </>
      )}
    </>
  );
}

export default PlpProductBanner;
